<script setup>
// InteliChat #41 - the always-visible INTEL CENTER (ground truth).
// A persistent right-edge rail across every workspace. Three sections,
// top to bottom = most urgent to least (charlie's finalized spec):
//   NEEDS YOU NOW - here now, waiting on a reply (hot accent, loudest)
//   NEW           - just came in, unclaimed (hot accent)
//   MISSED        - waited too long or left (calm, never the hot accent)
// Correct even if every alert channel is off. Desktop only; phones get
// NewChatToast + the live bottom-nav badge.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useNeverMiss } from './useNeverMiss';
import IntelHudRow from './IntelHudRow.vue';
import AlertControls from './AlertControls.vue';

// cap the preview rows per section so all three (and their live counts)
// stay glanceable at once - the count in each header is the ground truth;
// the rows are a top-N preview with a "+N more" jump.
const MAX_URGENT = 6;
const MAX_NEW = 6;
const MAX_MISSED = 4;

const {
  now,
  needsYouNow,
  newIncoming,
  missed,
  needsYouNowCount,
  newCount,
  missedCount,
  actNowCount,
  prefs,
  setPref,
  toggleSound,
  navigate,
  engine,
} = useNeverMiss();

// collapse (persisted lightly in localStorage; default expanded so the
// ground truth is glanceable by default).
const collapsed = ref(localStorage.getItem('ic41-hud-collapsed') === '1');
const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
  localStorage.setItem('ic41-hud-collapsed', collapsed.value ? '1' : '0');
};

// restrained pulse when the ACT-NOW total climbs (a new live-urgent chat
// entered NEEDS YOU NOW or NEW). Reduced-motion safe via CSS.
const pulsing = ref(false);
let pulseTimer = null;
watch(actNowCount, (n, prev) => {
  if (n > (prev || 0)) {
    pulsing.value = true;
    if (pulseTimer) clearTimeout(pulseTimer);
    pulseTimer = setTimeout(() => {
      pulsing.value = false;
    }, 2400);
  }
});

// top-N previews + overflow counts
const urgentShown = computed(() => needsYouNow.value.slice(0, MAX_URGENT));
const urgentMore = computed(() => Math.max(0, needsYouNowCount.value - MAX_URGENT));
const newShown = computed(() => newIncoming.value.slice(0, MAX_NEW));
const newMore = computed(() => Math.max(0, newCount.value - MAX_NEW));
const missedShown = computed(() => missed.value.slice(0, MAX_MISSED));
const missedMore = computed(() => Math.max(0, missedCount.value - MAX_MISSED));

const headSub = computed(() =>
  actNowCount.value > 0 ? `${actNowCount.value} to handle now` : 'All clear'
);

const route = useRoute();
const router = useRouter();
const openInbox = () =>
  router.push({ name: 'intelichat_inbox', params: { accountId: route.params.accountId, conversationId: '' } }).catch(() => {});

const onOpen = id => navigate(id);
// #41 Missed -> its durable ticket (Build #47 Phase 2): a missed chat becomes a
// trackable ticket in the same store the Magento admin sees. Deep-link into the
// Tickets workspace focused on THIS conversation's ticket (matched by conversation_id).
const onTicket = id =>
  router
    .push({ name: 'intelichat_tickets', params: { accountId: route.params.accountId }, query: { focus: id } })
    .catch(() => {});

// reserve content width when the rail is docked+expanded on wide screens
// so it never overlaps the conversation/inbox area (no clipped content).
const applyBodyClass = () => {
  const on = !collapsed.value && window.innerWidth >= 901;
  document.body.classList.toggle('ic41-hud-open', on);
};
watch(collapsed, applyBodyClass);
onMounted(() => {
  applyBodyClass();
  window.addEventListener('resize', applyBodyClass);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', applyBodyClass);
  document.body.classList.remove('ic41-hud-open');
});
</script>

<template>
  <aside
    class="ic41-hud"
    :class="{ 'ic41-hud--collapsed': collapsed }"
    data-il="ic41-hud"
    aria-label="Intel Center - live chats"
  >
    <button
      class="ic41-hud__tab"
      type="button"
      :title="collapsed ? 'Show Intel Center' : 'Collapse Intel Center'"
      @click="toggleCollapsed"
    >
      <span class="ic41-hud__tab-i" aria-hidden="true">{{ collapsed ? '‹' : '›' }}</span>
      <span v-if="collapsed && actNowCount" class="ic41-hud__tab-badge">{{ actNowCount > 9 ? '9+' : actNowCount }}</span>
    </button>

    <div v-if="!collapsed" class="ic41-hud__inner">
      <header class="ic41-hud__head">
        <div class="ic41-hud__title">
          <span class="ic41-hud__title-t">Intel Center</span>
          <span class="ic41-hud__title-s">{{ headSub }}</span>
        </div>
        <AlertControls
          :prefs="prefs"
          :engine="engine"
          @set-pref="setPref"
          @toggle-sound="toggleSound"
        />
      </header>

      <div class="ic41-hud__scroll">
        <!-- NEEDS YOU NOW (hero, top, loudest) -->
        <section
          class="ic41-hud__sec ic41-hud__sec--urgent"
          :class="{ 'ic41-hud__sec--pulse': pulsing }"
        >
          <div class="ic41-hud__sec-h">
            <span class="ic41-hud__sec-dot ic41-hud__sec-dot--urgent" />
            <span class="ic41-hud__sec-t">Needs you now</span>
            <span class="ic41-hud__sec-c ic41-hud__sec-c--urgent">{{ needsYouNowCount }}</span>
          </div>
          <p class="ic41-hud__sec-sub">Online and waiting on a reply.</p>
          <IntelHudRow
            v-for="row in urgentShown"
            :key="row.id"
            :row="row"
            :now="now"
            emphasis
            @open="onOpen"
          />
          <button v-if="urgentMore" class="ic41-hud__more" type="button" @click="openInbox">
            +{{ urgentMore }} more waiting
          </button>
          <p v-if="!needsYouNowCount" class="ic41-hud__empty">You're all caught up. No one is waiting.</p>
        </section>

        <!-- NEW (directly below, also loud) -->
        <section class="ic41-hud__sec ic41-hud__sec--new">
          <div class="ic41-hud__sec-h">
            <span class="ic41-hud__sec-dot ic41-hud__sec-dot--new" />
            <span class="ic41-hud__sec-t">New</span>
            <span class="ic41-hud__sec-c ic41-hud__sec-c--new">{{ newCount }}</span>
          </div>
          <p class="ic41-hud__sec-sub">Just came in, no one has answered yet.</p>
          <IntelHudRow
            v-for="row in newShown"
            :key="row.id"
            :row="row"
            :now="now"
            @open="onOpen"
          />
          <button v-if="newMore" class="ic41-hud__more" type="button" @click="openInbox">
            +{{ newMore }} more new
          </button>
          <p v-if="!newCount" class="ic41-hud__empty">No new chats right now.</p>
        </section>

        <!-- MISSED (below, calm but persistent) -->
        <section class="ic41-hud__sec">
          <div class="ic41-hud__sec-h">
            <span class="ic41-hud__sec-dot ic41-hud__sec-dot--missed" />
            <span class="ic41-hud__sec-t">Missed</span>
            <span class="ic41-hud__sec-c ic41-hud__sec-c--missed">{{ missedCount }}</span>
          </div>
          <p class="ic41-hud__sec-sub">Waited too long or left. Follow up.</p>
          <IntelHudRow
            v-for="row in missedShown"
            :key="row.id"
            :row="row"
            :now="now"
            link-ticket
            @open="onOpen"
            @ticket="onTicket"
          />
          <button v-if="missedMore" class="ic41-hud__more" type="button" @click="openInbox">
            +{{ missedMore }} more
          </button>
          <p v-if="!missedCount" class="ic41-hud__empty">Nothing missed. You're on top of it.</p>
        </section>
      </div>
    </div>
  </aside>
</template>

<style scoped lang="scss">
.ic41-hud {
  position: fixed;
  top: 64px;
  right: 0;
  bottom: 0;
  width: 288px;
  z-index: 40;
  display: flex;
  font-family: var(--il-f, system-ui, sans-serif);
  pointer-events: none; // let the tab + inner reclaim events

  &--collapsed {
    width: 0;
  }
}

.ic41-hud__tab {
  pointer-events: auto;
  position: absolute;
  left: -22px;
  top: 24px;
  width: 22px;
  height: 46px;
  border: 1px solid var(--il-line-2, rgba(20, 24, 45, 0.15));
  border-right: none;
  border-radius: 10px 0 0 10px;
  background: var(--il-card, #fff);
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: var(--il-sh, 0 1px 2px rgba(22, 26, 44, 0.04));

  .ic41-hud__tab-i {
    font-size: 16px;
    color: var(--il-ink-3a, #6b7290);
  }
  .ic41-hud__tab-badge {
    position: absolute;
    top: -6px;
    left: -6px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--il-accent-2, #ec4899);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: grid;
    place-items: center;
  }
}

.ic41-hud__inner {
  pointer-events: auto;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--il-paper, #f5f6fa);
  border-left: 1px solid var(--il-line-2, rgba(20, 24, 45, 0.15));
}

.ic41-hud__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 10px;
  border-bottom: 1px solid var(--il-line, rgba(20, 24, 45, 0.09));
}

.ic41-hud__title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  .ic41-hud__title-t {
    font-family: var(--il-fd, sans-serif);
    font-weight: 750;
    font-size: 15px;
    color: var(--il-ink, #161a2c);
  }
  .ic41-hud__title-s {
    font-size: 11px;
    font-weight: 600;
    color: var(--il-ink-3a, #6b7290);
  }
}

.ic41-hud__scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ic41-hud__sec {
  background: var(--il-card, #fff);
  border: 1px solid var(--il-line, rgba(20, 24, 45, 0.09));
  border-radius: var(--il-r, 14px);
  padding: 8px;

  // the two live act-now sections carry the hot accent ring; MISSED does
  // not (calm - "hot = a live person is waiting" stays unambiguous).
  &--urgent {
    border-color: var(--il-accent-line, #e4d4f7);
    box-shadow: 0 0 0 1px var(--il-accent-line, #e4d4f7);
  }
  &--new {
    border-color: var(--il-accent-line, #e4d4f7);
  }
  &--pulse {
    animation: ic41-pulse 0.8s ease-out 2;
  }
}

.ic41-hud__sec-h {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 6px 2px;
}

.ic41-hud__sec-sub {
  margin: 0 0 6px;
  padding: 0 6px;
  font-size: 11px;
  color: var(--il-ink-3a, #6b7290);
}

.ic41-hud__sec-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--il-silver-2, #9aa0ae);
  &--urgent {
    background: var(--il-accent-2, #ec4899);
  }
  &--new {
    background: var(--il-accent-2, #ec4899);
  }
  &--missed {
    background: var(--il-warn, #a9741a);
  }
}

.ic41-hud__sec-t {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--il-ink-2, #4a5170);
}

.ic41-hud__sec-c {
  font-family: var(--il-fm, monospace);
  font-size: 12px;
  font-weight: 700;
  min-width: 20px;
  text-align: center;
  padding: 1px 6px;
  border-radius: 7px;
  background: var(--il-silver-soft, #edeff3);
  color: var(--il-ink-3a, #6b7290);

  &--urgent,
  &--new {
    background: var(--il-accent-2, #ec4899);
    color: #fff;
  }
  &--missed {
    background: var(--il-warn-soft, #f6eede);
    color: var(--il-warn, #a9741a);
  }
}

.ic41-hud__empty {
  font-size: 12px;
  color: var(--il-ink-3a, #6b7290);
  padding: 4px 8px 6px;
  margin: 0;
}

.ic41-hud__more {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 650;
  color: var(--il-accent, #8b3df0);
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: var(--il-silver-soft, #edeff3);
  }
}

@keyframes ic41-pulse {
  0% {
    box-shadow: 0 0 0 1px var(--il-accent-line, #e4d4f7), 0 0 0 0 rgba(236, 72, 153, 0.5);
  }
  70% {
    box-shadow: 0 0 0 1px var(--il-accent-line, #e4d4f7), 0 0 0 10px rgba(236, 72, 153, 0);
  }
  100% {
    box-shadow: 0 0 0 1px var(--il-accent-line, #e4d4f7), 0 0 0 0 rgba(236, 72, 153, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ic41-hud__sec--pulse {
    animation: none;
    outline: 2px solid var(--il-accent-2, #ec4899);
    outline-offset: -1px;
  }
}

/* Phones: the rail is hidden - NewChatToast + the live bottom-nav badge
   carry the signal there (charlie's mobile spec). */
@media (max-width: 767.98px) {
  .ic41-hud {
    display: none;
  }
}
</style>

<!-- Non-scoped: reserve content width so the docked rail never OVERLAPS the
     conversation/inbox area (fixes the clipped "All"/macros GPT flagged).
     Only when expanded + wide; the collapsed tab is a thin edge sliver. -->
<style lang="scss">
body.ic41-hud-open .il-app .il-main {
  transition: padding-right 0.16s ease;
}
@media (min-width: 901px) {
  body.ic41-hud-open .il-app .il-main {
    padding-right: 288px;
  }
}
/* the rail hides below 768; between 768-900 it stays collapsed by default
   (thin tab), so no reservation needed there. */
</style>
